// Shared Web Audio plumbing for bgm.ts and sfx.ts: one AudioContext, one
// master GainNode (everything routes through it so the sound-on setting mutes
// both BGM and SFX together), and the autoplay-unlock/mute wiring.

import { getSoundOn, SOUND_CHANGE_EVENT } from "../../gamzone/settings.ts";

const MUTE_FADE_S = 0.3;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let unlockArmed = false;

function armUnlock(): void {
  if (unlockArmed) return;
  unlockArmed = true;
  const unlock = () => ctx?.resume().catch(() => {});
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
}

function setMuted(muted: boolean): void {
  if (!ctx || !master) return;
  const now = ctx.currentTime;
  master.gain.cancelScheduledValues(now);
  master.gain.setValueAtTime(master.gain.value, now);
  master.gain.linearRampToValueAtTime(muted ? 0 : 1, now + MUTE_FADE_S);
  if (!muted) ctx.resume().catch(() => {});
}

/** Lazily creates the shared AudioContext + master gain on first use (browsers
 *  refuse to create one before a user gesture has a chance to unlock it). */
export function getAudioCtx(): { ctx: AudioContext; master: GainNode } {
  if (!ctx) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = getSoundOn() ? 1 : 0;
    master.connect(ctx.destination);
    armUnlock();
    window.addEventListener(SOUND_CHANGE_EVENT, ((e: CustomEvent<boolean>) => {
      setMuted(!e.detail);
    }) as EventListener);
  }
  return { ctx, master: master! };
}
